/**
 * Web Speech API wrapper with Capacitor SpeechRecognition fallback.
 * On native platforms uses Capacitor; on web uses the browser API.
 */

// Local interface definitions for Web Speech API (not always in tsconfig lib)
interface SpeechRecognitionResultItem {
  transcript: string;
}
interface SpeechRecognitionResultEntry {
  isFinal: boolean;
  [index: number]: SpeechRecognitionResultItem;
}
interface SpeechRecognitionResultList {
  length: number;
  [index: number]: SpeechRecognitionResultEntry;
}
interface WebSpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
}
interface SpeechRecognitionInstance {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: WebSpeechRecognitionEvent) => void) | null;
  // Use Event base type to avoid strictFunctionTypes conflicts with DOM lib
  onerror: ((event: Event & { error?: string }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
}
type SpeechRecognitionConstructor = new () => SpeechRecognitionInstance;

export type SpeechRecognitionResult = {
  transcript: string;
  isFinal: boolean;
};

export type SpeechRecognitionCallbacks = {
  onResult: (result: SpeechRecognitionResult) => void;
  onEnd: () => void;
  onError: (error: string) => void;
};

export type StopFn = () => void;

/** Request mic permission. Returns true if granted. */
export async function requestSpeechPermission(): Promise<boolean> {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach((t) => t.stop());
    return true;
  } catch {
    return false;
  }
}

/** Start speech recognition. Returns a stop function. */
export function startSpeechRecognition(callbacks: SpeechRecognitionCallbacks): StopFn {
  const w = window as typeof window & {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  };
  const Ctor = w.SpeechRecognition ?? w.webkitSpeechRecognition;

  if (!Ctor) {
    callbacks.onError('Speech recognition is not supported in this browser.');
    return () => {};
  }

  const recognition = new Ctor();
  recognition.continuous = true;
  recognition.interimResults = true;
  recognition.lang = 'en-US';

  recognition.onresult = (event: WebSpeechRecognitionEvent) => {
    const last = event.results[event.results.length - 1];
    callbacks.onResult({
      transcript: last[0].transcript,
      isFinal: last.isFinal,
    });
  };

  recognition.onerror = (event: Event & { error?: string }) => {
    callbacks.onError(event.error ?? 'Unknown speech recognition error');
  };

  recognition.onend = () => {
    callbacks.onEnd();
  };

  recognition.start();
  return () => recognition.stop();
}
